/**
 * Layout-level right-slot enforcement.
 *
 * `_panel-mutex.ts` remains the preferred user-action path because it records
 * intent and telemetry. This registry is the safety net for legacy and
 * automatic store paths: when one right-slot owner opens, all other owners close.
 */
export type RightSlotOwner =
  'project-hub' | 'artifact-drawer' | 'global-dock' | 'workspace-inspector';

type CloseFn = () => void;

const closers: Partial<Record<RightSlotOwner, CloseFn>> = {};

/**
 * There is no exemption from this mutex. An earlier version shielded
 * `global-dock` from the `artifact-drawer`'s close sweep so a panel-hosted
 * agent turn's live `useChat` state could not be unmounted by its own
 * generated artifact. The founder's ruling (MASTER-PLAN §1.4) replaces that
 * shield: the panel never opens another panel at all — a panel turn that
 * produces an artifact renders it INLINE in the thread
 * (`GenerateDocumentPart`'s `presentation === 'panel'` branch), so the
 * artifact-drawer open this shield used to survive never happens in the
 * first place. Two surfaces contending for one slot stays a bug in every
 * direction.
 */
export function registerRightSlotCloser(owner: RightSlotOwner, close: CloseFn): void {
  closers[owner] = close;
}

export function closeOtherRightSlotSurfaces(self: RightSlotOwner): void {
  for (const [owner, close] of Object.entries(closers) as Array<[RightSlotOwner, CloseFn]>) {
    if (owner === self) continue;
    try {
      close();
    } catch {
      /* never let one stale panel block another from opening */
    }
  }
}
